import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Filter, Calendar, Search, X, Star } from 'lucide-react';
import { WorkspaceLayout, WorkspaceHeader, WorkspaceContent, WorkspaceToolbar, WorkspaceSummary, SummaryCard } from './WorkspaceLayout';
import ReportGrid from './ReportGrid';
import ExportMenu from './ExportMenu';
import ColumnSelectorPanel, { ColumnSettingsButton } from './ColumnSelectorPanel';
import api from '@/services/api';
import { FormHelp } from '../help-engine/FormHelp';

/**
 * ReportLayout - Unified report layout component
 * All reports must use this component for consistent behavior
 */
const ReportLayout = ({
  // Report identification
  reportCode,
  reportTitle,
  reportSubtitle,
  reportIcon,
  
  // Data
  data = [],
  loading = false,
  error = null,
  
  // Callbacks
  onRefresh = null,
  onDataLoad = null,
  
  // Configuration
  theme = 'light',
  userId = 'default',
  
  // Filters
  filters = null,
  onFilterChange = null,
  
  // Summary cards
  summaryCards = [],
  
  // Custom toolbar items
  toolbarLeft = null,
  toolbarRight = null,
  
  // Grid options
  showRowNumbers = false,
  onRowClick = null,
  selectedRow = null,
  
  // Pagination
  pagination = null,
  onPageChange = null,
  onPageSizeChange = null,
  pageSizeOptions = [10, 20, 50, 100],
  
  // Children for custom content
  children
}) => {
  const isDark = theme === 'dark';
  
  // Report configuration from database
  const [reportConfig, setReportConfig] = useState(null);
  const [columns, setColumns] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  
  // Column settings panel
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  
  // Favorites
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Sorting
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Load report configuration and track access
  useEffect(() => {
    if (reportCode) {
      loadReportConfig();
      trackReportAccess();
      checkIfFavorite();
    }
  }, [reportCode, userId]);

  // Track report access for "Frequently Used" feature
  const trackReportAccess = async () => {
    try {
      await api.post('/api/report-engine/track-access', { userId, reportCode });
    } catch (error) {
      // Silently fail - tracking is non-critical
      console.debug('Track access failed:', error);
    }
  };

  // Check if report is in favorites
  const checkIfFavorite = async () => {
    try {
      const response = await api.get(`/api/report-engine/favorites/${userId}`);
      if (response.data.success) {
        const favorites = response.data.data || [];
        setIsFavorite(favorites.some(f => f.code === reportCode));
      }
    } catch (error) {
      console.debug('Check favorite failed:', error);
    }
  };

  // Toggle favorite status
  const handleToggleFavorite = async () => {
    try {
      if (isFavorite) {
        await api.delete(`/api/report-engine/favorites/${userId}/${reportCode}`);
        setIsFavorite(false);
      } else {
        await api.post('/api/report-engine/favorites', { userId, reportCode });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Toggle favorite failed:', error);
    }
  };

  const loadReportConfig = async () => {
    setConfigLoading(true);
    try {
      const response = await api.get(`/api/report-engine/config/${reportCode}?userId=${userId}`);
      if (response.data.success) {
        setReportConfig(response.data.data.report);
        setColumns(response.data.data.columns);
      }
    } catch (error) {
      console.error('Error loading report config:', error);
      // Use default columns if config not found
    } finally {
      setConfigLoading(false);
    }
  };

  // Save user preferences
  const saveUserPreferences = async (updatedColumns) => {
    if (!reportCode) return;
    
    try {
      await api.post('/api/report-engine/preferences', {
        userId,
        reportCode,
        columns: updatedColumns
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  // Reset preferences to default
  const resetPreferences = async () => {
    if (!reportCode) return;
    
    try {
      await api.delete(`/api/report-engine/preferences/${reportCode}/${userId}`);
      await loadReportConfig(); // Reload default config
    } catch (error) {
      console.error('Error resetting preferences:', error);
    }
  };

  // Handle column changes
  const handleColumnsChange = (newColumns) => {
    setColumns(newColumns);
  };

  // Handle column resize
  const handleColumnResize = (field, width) => {
    const updatedColumns = columns.map(col =>
      col.field === field ? { ...col, width } : col
    );
    setColumns(updatedColumns);
    saveUserPreferences(updatedColumns);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortColumn === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(field);
      setSortDirection('asc');
    }
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!sortColumn) return data;
    
    return [...data].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      const comparison = typeof aVal === 'number' 
        ? aVal - bVal 
        : String(aVal).localeCompare(String(bVal));
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortColumn, sortDirection]);

  // Export filename
  const exportFilename = `${reportCode || 'report'}_${new Date().toISOString().split('T')[0]}`;

  return (
    <FormHelp formName={reportCode || reportTitle || 'Report'}>
    <div data-dev-component={reportCode || 'Report'} data-dev-file="components/report-engine/ReportLayout.jsx" style={{ display: 'contents' }}>
    <WorkspaceLayout theme={theme}>
      {/* Header */}
      <WorkspaceHeader theme={theme}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {reportIcon && (
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                  {reportIcon}
                </div>
              )}
              <div>
                <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {reportTitle || reportConfig?.name || 'Report'}
                </h1>
                {reportSubtitle && (
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                    {reportSubtitle}
                  </p>
                )}
              </div>
            </div>
            
            {/* Header Actions */}
            <div className="flex items-center gap-2">
              {/* Favorite Button */}
              <button
                onClick={handleToggleFavorite}
                className={`p-2 rounded-lg transition-colors ${
                  isFavorite 
                    ? 'text-amber-500' 
                    : isDark ? 'text-slate-500 hover:text-amber-400 hover:bg-white/10' : 'text-gray-400 hover:text-amber-500 hover:bg-gray-100'
                }`}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                data-testid="report-favorite-btn"
              >
                <Star size={18} className={isFavorite ? 'fill-current' : ''} />
              </button>
              
              {toolbarRight}
              <ColumnSettingsButton onClick={() => setShowColumnSettings(true)} theme={theme} />
              <ExportMenu
                data={sortedData}
                columns={columns}
                filename={exportFilename}
                reportTitle={reportTitle || reportConfig?.name || 'Report'}
                theme={theme}
                disabled={loading || data.length === 0}
              />
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={loading}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
                  title="Refresh"
                >
                  <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
              )}
            </div>
          </div>
        </div>
      </WorkspaceHeader>

      {/* Toolbar with Filters */}
      {(filters || toolbarLeft) && (
        <WorkspaceToolbar theme={theme}>
          {toolbarLeft}
          {filters}
        </WorkspaceToolbar>
      )}

      {/* Summary Cards */}
      {summaryCards.length > 0 && (
        <WorkspaceSummary theme={theme}>
          {summaryCards.map((card, index) => (
            <SummaryCard key={index} {...card} theme={theme} />
          ))}
        </WorkspaceSummary>
      )}

      {/* Content */}
      <WorkspaceContent theme={theme}>
        {children || (
          <div className="flex-1 flex flex-col p-4 min-h-0">
            <ReportGrid
              columns={columns}
              data={sortedData}
              theme={theme}
              loading={loading || configLoading}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onColumnResize={handleColumnResize}
              showRowNumbers={showRowNumbers}
              onRowClick={onRowClick}
              selectedRow={selectedRow}
              emptyMessage={error || 'No data found'}
              pagination={pagination}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              pageSizeOptions={pageSizeOptions}
            />
          </div>
        )}
      </WorkspaceContent>

      {/* Column Settings Panel */}
      <ColumnSelectorPanel
        columns={columns}
        onChange={handleColumnsChange}
        onReset={resetPreferences}
        onSave={saveUserPreferences}
        theme={theme}
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
      />
    </WorkspaceLayout>
    </div>
    </FormHelp>
  );
};

export default ReportLayout;
